import api from './api';

const monitoringService = {
  // ---- EIA condition-based monitoring (existing regulatory path) ----

  /**
   * Get monitoring tasks generated from EIA conditions.
   */
  async getTasks(params = {}) {
    const response = await api.get('/monitoring/tasks', { params });
    return response.data;
  },

  /**
   * Submit a monitoring record against a task.
   */
  async submitRecord(taskId, submissionData) {
    const response = await api.post('/monitoring/records', { taskId, submissionData });
    return response.data;
  },

  /**
   * Get monitoring records, optionally filtered by task/status.
   */
  async getRecords(params = {}) {
    const response = await api.get('/monitoring/records', { params });
    return response.data;
  },

  // ---- Configurable monitoring parameters (org/project-defined) ----

  /**
   * List configured monitoring parameters, optionally filtered by project/category.
   */
  async getParameters(params = {}) {
    const response = await api.get('/monitoring-parameters', { params });
    return response.data;
  },

  /**
   * Get a single monitoring parameter.
   */
  async getParameter(id) {
    const response = await api.get(`/monitoring-parameters/${id}`);
    return response.data;
  },

  /**
   * Create a new configurable monitoring parameter.
   */
  async createParameter(parameterData) {
    const response = await api.post('/monitoring-parameters', parameterData);
    return response.data;
  },

  /**
   * Update a monitoring parameter (thresholds, active state, etc).
   */
  async updateParameter(id, parameterData) {
    const response = await api.patch(`/monitoring-parameters/${id}`, parameterData);
    return response.data;
  },

  /**
   * Delete a monitoring parameter.
   */
  async deleteParameter(id) {
    const response = await api.delete(`/monitoring-parameters/${id}`);
    return response.data;
  },

  // ---- Daily / periodic monitoring entries ----

  /**
   * Submit a daily/periodic entry against a configurable parameter.
   */
  async submitEntry(entryData) {
    const response = await api.post('/monitoring/entries', entryData);
    return response.data;
  },

  /**
   * List monitoring entries, filterable by project/parameter/category/date range.
   */
  async getEntries(params = {}) {
    const response = await api.get('/monitoring/entries', { params });
    return response.data;
  },

  /**
   * Aggregated trend/graph data for entries (series, by category, by parameter).
   */
  async getEntriesSummary(params = {}) {
    const response = await api.get('/monitoring/entries/summary', { params });
    return response.data;
  },

  // ---- Evidence attached to daily log entries ----

  /**
   * Upload a photo/document as evidence for a daily log entry.
   */
  async uploadEntryEvidence(entryId, file, fileType = 'photo') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);
    const response = await api.post(`/evidence/entry/${entryId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * List evidence attached to a daily log entry.
   */
  async getEntryEvidence(entryId) {
    const response = await api.get(`/evidence/entry/${entryId}`);
    return response.data;
  },

  /**
   * Delete a piece of evidence (removes it from Cloudinary too).
   */
  async deleteEvidence(evidenceId) {
    const response = await api.delete(`/evidence/${evidenceId}`);
    return response.data;
  },
};

export default monitoringService;
